import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useAuth } from '../contexts/AuthContext';
import { Toaster } from './ui/sonner';
import { Menu, X } from 'lucide-react';

export const Layout = () => {
  const { user, isApproved } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const publicRoutes = ['/', '/login', '/register'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Pages qui doivent occuper tout l'espace dispo (sans padding ni scroll global)
  const fullscreenRoutes = ['/dresstek'];
  const isFullscreen = fullscreenRoutes.includes(location.pathname);

  // Close mobile menu when route changes
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  if (isPublicRoute) {
    return (
      <>
        <Outlet />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 w-64 z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onClose={() => setIsMobileMenuOpen(false)} />
      </aside>
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <Sidebar />
      </aside>
      
      {/* Main content */}
      {isFullscreen ? (
        /* Dresstek : pas de TopBar, iframe plein écran, burger flottant sur mobile */
        <div className="md:pl-64 h-screen overflow-hidden">
          {/* Burger flottant — visible uniquement sur mobile (desktop : sidebar toujours visible) */}
          <button
            className="fixed top-3 left-3 z-40 md:hidden p-2 rounded-lg bg-white/90 backdrop-blur-sm shadow border border-stone-200"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu size={22} className="text-[#2E4A3D]" />
          </button>

          <main className="h-full overflow-hidden">
            <Outlet />
          </main>
        </div>
      ) : (
        /* Toutes les autres pages : layout normal avec TopBar */
        <div className="md:pl-64">
          <TopBar
            onMenuClick={() => setIsMobileMenuOpen(true)}
            showMenuButton={true}
          />

          {/* Approval banner */}
          {user && !isApproved() && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
              <div className="flex items-center gap-2 text-amber-800 text-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>
                  {user.is_approved === 'en_attente'
                    ? 'Votre compte est en attente de validation par un administrateur.'
                    : 'Votre compte n\'est pas actif. Contactez l\'administration.'}
                </span>
              </div>
            </div>
          )}

          <main className="page-container pb-4">
            <Outlet />
          </main>
        </div>
      )}
      
      <Toaster position="top-center" richColors />
    </div>
  );
};
